import { useEffect, useMemo, useState } from "react";
import AxiosInstance from "../Home/AxiosInstance";
import { useNavigate, useParams } from "react-router-dom";
import { useTable, useGlobalFilter, useSortBy, useResizeColumns, usePagination } from "react-table";
import { FiEdit, FiTrash, FiArrowLeft } from "react-icons/fi";
import TrainerHeader from "./TrainerHeader";
import Footer from "../Home/Footer";
import "./CourseSubTopics.css";
import "./trainerPortal.css";

export default function CourseSubTopics() {
  const navigate = useNavigate();
  const { topic_id } = useParams();

  const [subTopics, setSubTopics] = useState([]);
  const [subTopicFilter, setSubTopicFilter] = useState("");
  const [editingSubTopic, setEditingSubTopic] = useState(null);
  const [editedSubTopic, setEditedSubTopic] = useState({});
  const [subTopicGlobalFilter, setSubTopicGlobalFilter] = useState("");
  const [viewingContent, setViewingContent] = useState(null);

  useEffect(() => {
    // Fetch topics
    AxiosInstance
      .get(`companies/sub-topics/${topic_id}/`)
      .then((response) => {
        setSubTopics(response.data);
      })
      .catch((error) => {
        console.error("Error fetching topics:", error);
      });
  }, [topic_id]);

  function formatContent(text) {
    if (!text) return "";

    const lines = text
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line !== "");
    const output = [];

    let listBuffer = [];

    const flushList = () => {
      if (listBuffer.length > 0) {
        output.push(
          `<ul>${listBuffer.map((item) => `<li>${item}</li>`).join("")}</ul>`
        );
        listBuffer = [];
      }
    };

    lines.forEach((line) => {
      if (/^[-*]\s+/.test(line)) {
        // It's a bullet point
        listBuffer.push(line.replace(/^[-*]\s+/, "").trim());
      } else {
        // Not a bullet: flush previous list and add paragraph
        flushList();
        output.push(`<p>${line}</p>`);
      }
    });

    // Flush any remaining bullets
    flushList();

    return output.join("");
  }

  const subTopicColumns = useMemo(
    () => [
      { Header: "Topic Name", accessor: "topic.topic_name", resizable: true },
      { Header: "Sub Topic Name", accessor: "sub_topic_name", resizable: true },
      {
        Header: "Sub Topic Content",
        accessor: "sub_topic_content",
        resizable: true,
        Cell: ({ value, row }) => {
          if (!value) return null;
          return (
            <div className="topic-content-cell">
              <div
                className="topic-content-preview"
                dangerouslySetInnerHTML={{ __html: formatContent(value) }}
              />
              <button
                type="button"
                className="topic-content-view-more"
                onClick={() =>
                  setViewingContent({
                    title: row.original.sub_topic_name,
                    content: value,
                  })
                }
              >
                view more
              </button>
            </div>
          );
        },
      },
      { Header: "Video Link", accessor: "video_link", resizable: true },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => {
                const original = row.original;
                setEditingSubTopic(original);
                setEditedSubTopic(original);
              }}
            >
              <FiEdit />
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleDeleteSubTopic(row.original.sub_topic_id)}
            >
              <FiTrash />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const {
    getTableProps: getSubTopicTableProps,
    getTableBodyProps: getSubTopicTableBodyProps,
    headerGroups: subTopicHeaderGroups,
    prepareRow: prepareSubTopicRow,
    page: subTopicPage,
    canPreviousPage: canPreviousSubTopicPage,
    canNextPage: canNextSubTopicPage,
    pageOptions: subTopicPageOptions,
    nextPage: nextSubTopicPage,
    previousPage: previousSubTopicPage,
    state: { pageIndex: subTopicPageIndex },
    setGlobalFilter: setSubTopicGlobalFilterFunc,
  } = useTable(
    {
      columns: subTopicColumns,
      data: subTopics,
      initialState: { pageSize: 8 },
    },
    useGlobalFilter,
    useSortBy,
    useResizeColumns,
    usePagination
  );

  useEffect(() => {
    setSubTopicGlobalFilterFunc(subTopicGlobalFilter);
  }, [subTopicGlobalFilter, setSubTopicGlobalFilterFunc]);

  const handleSaveSubTopic = async () => {
  const formData = new FormData();
  formData.append("sub_topic_name", editedSubTopic.sub_topic_name);
  formData.append("topic", editedSubTopic.topic.topic_id);
  formData.append("sub_topic_content", editedSubTopic.sub_topic_content);

  if (editedSubTopic.video_file) {
    formData.append("video_link", editedSubTopic.video_file);
  }

  try {
    const response = await AxiosInstance.put(
      `companies/sub-topic/${editedSubTopic.sub_topic_id}/`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    // Update the state immediately
    setSubTopics((prev) =>
  prev.map((item) =>
    item.sub_topic_id === editedSubTopic.sub_topic_id
      ? { 
          ...response.data, 
          topic: item.topic // preserve the topic object
        }
      : item
  )
);

    setEditingSubTopic(null); // close the edit form

  } catch (error) {
    console.error("Update error:", error);
  }
};


  const handleDeleteSubTopic = (subTopicId) => {
  const confirmDelete = window.confirm(
    "Are you sure you want to delete this sub topic?"
  );
  if (!confirmDelete) return;

  AxiosInstance
    .delete(`companies/topic/sub-topics/${subTopicId}/`)
    .then(() => {
      // Remove the deleted subtopic from state immediately
      setSubTopics((prev) =>
        prev.filter((subTopic) => subTopic.sub_topic_id !== subTopicId)
      );
    })
    .catch((error) => console.error("Error deleting sub topic:", error));
};


  return (
    <div className="TrainerPage als-trainer-portal">
      <TrainerHeader />
      <div className="trainer-container py-4">
        <div className="mb-3">
          <button
            className="navigate-button btn btn-outline-primary d-flex align-items-center"
            onClick={() => navigate(-1)}
            style={{ gap: "6px" }}
          >
            <FiArrowLeft /> Back
          </button>
        </div>
        <h2 className="section-heading">Sub Topics</h2>
        <div className="card-custom">
          <div className="d-flex justify-content-between mb-3">
            <input
              type="text"
              className="form-control w-50"
              value={subTopicFilter}
              onChange={(e) => {
                setSubTopicFilter(e.target.value);
                setSubTopicGlobalFilter(e.target.value);
              }}
              placeholder="Search sub topic..."
            />
            <button
              className="add-button"
              onClick={() => navigate(`/sub-topic`)}
            >
              Add
            </button>
          </div>

          <div className="table-responsive">
            <table
              {...getSubTopicTableProps()}
              className="table table-bordered table-striped"
            >
              <thead className="table-light">
                {subTopicHeaderGroups.map((headerGroup) => (
                  <tr
                    {...headerGroup.getHeaderGroupProps()}
                    key={headerGroup.id}
                  >
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                        key={column.id}
                      >
                        <div {...column.getHeaderProps()}>
                          {column.render("Header")}
                          {column.isSorted
                            ? column.isSortedDesc
                              ? " ▼"
                              : " ▲"
                            : ""}
                        </div>
                        {column.canResize && <div />}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getSubTopicTableBodyProps()}>
                {subTopicPage.length === 0 ? (
                  <tr>
                    <td colSpan={subTopicColumns.length}>
                      No sub topics found.
                    </td>
                  </tr>
                ) : (
                  subTopicPage.map((row) => {
                    prepareSubTopicRow(row);
                    return (
                      <tr {...row.getRowProps()} key={row.id}>
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()} key={cell.column.id}>
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            <div className="pagination d-flex align-items-center gap-2 mt-3">
              <button
                onClick={() => previousSubTopicPage()}
                disabled={!canPreviousSubTopicPage}
                className="btn btn-sm btn-outline-primary"
              >
                Previous
              </button>
              <span>
                Page
                <strong>{subTopicPageIndex + 1} </strong> of{" "}
                <strong>{subTopicPageOptions.length}</strong>
              </span>
              <button
                onClick={() => nextSubTopicPage()}
                disabled={!canNextSubTopicPage}
                className="btn btn-sm btn-outline-primary"
              >
                Next
              </button>
            </div>
          </div>
          {viewingContent && (
            <div
              className="modal show fade"
              style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
              tabIndex={-1}
              role="dialog"
              onClick={() => setViewingContent(null)}
            >
              <div
                className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
                role="document"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {viewingContent.title || "Sub Topic Content"}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={() => setViewingContent(null)}
                    />
                  </div>
                  <div className="modal-body">
                    <div
                      className="topic-content-full"
                      dangerouslySetInnerHTML={{
                        __html: formatContent(viewingContent.content),
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          {editingSubTopic && (
            <div
              className="modal show fade"
              style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
              tabIndex={-1}
              role="dialog"
            >
              <div className="modal-dialog" role="document">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Edit Sub Topic</h5>
                  </div>

                  <div className="modal-body">
                    <form>
                      <div className="mb-3">
                        <label htmlFor="topicName" className="form-label">
                          Topic Name
                        </label>
                        <input
                          type="text"
                          id="topicName"
                          className="form-control"
                          name="topic"
                          value={editedSubTopic.topic?.topic_name || ""}
                          onChange={(e) =>
                            setEditedSubTopic({
                              ...editedSubTopic,
                              topic: {
                                ...editedSubTopic.topic,
                                topic_name: e.target.value,
                              },
                            })
                          }
                          // If you want to disable editing topic name (recommended)
                          disabled
                        />
                      </div>

                      <div className="mb-3">
                        <label htmlFor="subTopicName" className="form-label">
                          Sub Topic Name
                        </label>
                        <input
                          type="text"
                          id="subTopicName"
                          className="form-control"
                          name="sub_topic_name"
                          value={editedSubTopic.sub_topic_name || ""}
                          onChange={(e) =>
                            setEditedSubTopic({
                              ...editedSubTopic,
                              sub_topic_name: e.target.value,
                            })
                          }
                        />
                      </div>

                      <div className="mb-3">
                        <label htmlFor="subTopicContent" className="form-label">
                          Sub Topic Content
                        </label>
                        <textarea
                          className="form-control"
                          name="sub_topic_content"
                          rows="6"
                          placeholder="Write sub-topic content here. Use - or * for bullets, or separate paragraphs with new lines."
                          value={editedSubTopic.sub_topic_content || ""}
                          onChange={(e) =>
                            setEditedSubTopic({
                              ...editedSubTopic,
                              sub_topic_content: e.target.value,
                            })
                          }
                        ></textarea>
                      </div>

                      <div className="mb-3">
                        <label htmlFor="videoFile" className="form-label">
                          Video File
                        </label>
                        <input
                          className="form-control"
                          type="file"
                          id="videoFile"
                          name="video_link"
                          onChange={(e) =>
                            setEditedSubTopic({
                              ...editedSubTopic,
                              video_file: e.target.files[0],
                            })
                          }
                        />
                        {editedSubTopic.video_link && (
                          <small className="form-text text-muted">
                            Current file:{" "}
                            {editedSubTopic.video_link.split("/").pop()}
                          </small>
                        )}
                      </div>
                    </form>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setEditingSubTopic(null)}
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSaveSubTopic}
                    >
                      Save changes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
